import Stack from '@mui/material/Stack'
import { Grid, styled, Divider, Typography, Paper, Box, Table, TableHead, TableBody, TableRow, TableCell, TableFooter } from '@mui/material'
import { useAppTranslation } from 'src/admin/core/hooks'
import { formatAsCurrency } from 'src/admin/core'
import { formatToShortLocalDate, formatToShortLocalDateTime } from 'src/admin/core/services/dateFunctions'
import { ConsolidatedData } from '../'
import { getAppBarState } from 'src/store'
import React, { useRef, forwardRef, useImperativeHandle } from 'react'
import jsPDF from 'jspdf'

const Label = styled(Grid)(({ theme }) => ({
  fontWeight: 'bold',
}))

export const PrintReport = forwardRef(({ data, fromDate, toDate, provider, enterpriseName, productName }: { data: ConsolidatedData[]; fromDate: string; toDate: string; provider: string; enterpriseName?: string; productName?: string }, ref) => {
  const { label } = useAppTranslation()
  const {
    user: { profile },
  } = getAppBarState().appState
  const firstRecord = data.length > 0 ? data[0] : null
  const now = new Date()

  // Calculate totals
  const totals = {
    sales: data.reduce((sum, row) => sum + (row.sales || 0), 0),
    cancelled: data.reduce((sum, row) => sum + (row.cancelled || 0), 0),
    payments: data.reduce((sum, row) => sum + (row.payments || 0), 0),
    amount: data.reduce((sum, row) => sum + (row.amount || 0), 0),
    surcharge: data.reduce((sum, row) => sum + (row.surcharge || 0), 0),
  }

  // Ref for table
  const tableRef = useRef<HTMLDivElement>(null)

  // Expose the ref through a method that can be accessed from the parent component
  useImperativeHandle(ref, () => ({
    getTableRef: () => tableRef,
    // We no longer need footerRef since we've integrated it into the table
    downloadReport: () => {
      if (tableRef.current) {
        downloadReportAsPDF(
          tableRef,
          null, // Pass null for footerRef since we don't use it anymore
          'balance-agencia',
          fromDate,
          toDate,
          provider,
          enterpriseName || '',
          productName || ''
        )
      }
    }
  }));

  return (
    <Box sx={{ padding: 2 }}>
      {firstRecord && (
        <Stack direction="row" spacing={2} style={{ padding: '10px' }}>
          <Grid container spacing={2}>
            <Label item xs={12} md={12} textAlign="center" style={{ display: 'grid' }}>
              <span>{label('BALANCE_POR_AGENCIA_TITULO') + (enterpriseName ? ' - ' + enterpriseName : '') + (productName ? ' - ' + productName : '')}</span>
            </Label>
            <Label item xs={12} md={12} textAlign="center" style={{ display: 'grid' }}>
              <Typography variant="body1" textAlign="center" style={{ fontSize: 12 }}>
                {`${label('DESDE')} ${formatToShortLocalDate(fromDate, 'YYYY-MM-DD')} ${label('HASTA')} ${formatToShortLocalDate(toDate, 'YYYY-MM-DD')} 
                ${provider?.length > 0 ? ` de ${provider}` : ''}`}
              </Typography>
            </Label>                                    
          </Grid>
        </Stack>
      )}

      <Paper elevation={0} sx={{ mt: 1, mb: 0 }}>
        {/* Table view without footer for PDF export */}
        <div ref={tableRef} data-ref="tableRef">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ backgroundColor: (theme) => theme.palette.grey[200] }}>
                <TableCell>{label('AGENCIAS')}</TableCell>
                <TableCell align="right">{label('VENTAS')}</TableCell>
                <TableCell align="right">{label('CANCELACIONES')}</TableCell>
                <TableCell align="right">{label('PAGOS')}</TableCell>
                <TableCell align="right">{label('BALANCE')}</TableCell>
                <TableCell align="right">{label('SOBRECARGO')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>{row.agencyWithCode}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{formatAsCurrency(row.sales, 0)}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{formatAsCurrency(row.cancelled, 0)}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{formatAsCurrency(row.payments, 0)}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{formatAsCurrency(row.amount, 0)}</TableCell>
                  <TableCell sx={{ textAlign: 'right' }}>{formatAsCurrency(row.surcharge, 0)}</TableCell>
                </TableRow>
              ))}
              {/* Footer row added directly to the table body */}
              <TableRow>
                <TableCell sx={{ fontWeight: 'bold', borderTop: '1px solid rgba(224, 224, 224, 1)' }}>{label('TOTALES')}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right', borderTop: '1px solid rgba(224, 224, 224, 1)' }}>{formatAsCurrency(totals.sales)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right', borderTop: '1px solid rgba(224, 224, 224, 1)' }}>{formatAsCurrency(totals.cancelled)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right', borderTop: '1px solid rgba(224, 224, 224, 1)' }}>{formatAsCurrency(totals.payments)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right', borderTop: '1px solid rgba(224, 224, 224, 1)' }}>{formatAsCurrency(totals.amount)}</TableCell>
                <TableCell sx={{ fontWeight: 'bold', textAlign: 'right', borderTop: '1px solid rgba(224, 224, 224, 1)' }}>{formatAsCurrency(totals.surcharge)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </div>
      </Paper>

      <Grid item xs={12} md={12} textAlign="right" style={{ marginTop: 10 }}>
        <Typography variant="body1" >
          {`${profile.given_name} ${formatToShortLocalDateTime(now, 'DD-MM-YYYY h:mm:ss a')}`}
        </Typography>
      </Grid>
    </Box>
  )
})

// Function to download the report as PDF
export const downloadReportAsPDF = async (
  tableRef: React.RefObject<HTMLElement>,
  footerRef?: React.RefObject<HTMLElement> | null,
  filename: string = 'balance-agencia',
  fromDateStr: string = '0000-00-00',
  toDateStr: string = '0000-00-00',
  provider: string = '',
  enterpriseName: string = '',
  productName: string = ''
) => {
  try {
    // Create new PDF
    const pdf = new jsPDF({
      orientation: 'portrait',
      unit: 'mm',
      format: 'letter',
    });
    
    // Page dimensions
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    
    // Margins
    const margins = {
      top: 20,
      bottom: 20,
      left: 15,
      right: 15,
    };
    
    // Get table elements
    const tableElement = tableRef.current;
    const headerRow = tableElement.querySelector('thead tr');
    const bodyRows = Array.from(tableElement.querySelectorAll('tbody tr'));
    
    // The last row in the body is now the totals row
    const totalRowIndex = bodyRows.length - 1;
    const dataRows = bodyRows.slice(0, totalRowIndex);
    const footerRow = bodyRows[totalRowIndex];
    
    if (!headerRow || bodyRows.length === 0) {
      throw new Error('Could not find necessary elements in the table');
    }
    
    // Table configuration
    const fontSize = 8; // Reduced from 9 to 8 for more compact text
    const lineHeight = fontSize * 0.8; // Reduced for more compact rows
    const rowsPerPage = Math.floor((pageHeight - margins.top - margins.bottom) / lineHeight);
    
    // Set font
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(fontSize);
    
    // Get column headers
    const headers = Array.from(headerRow.querySelectorAll('th')).map(th => th.textContent || '');
    
    // Calculate width for each column (proportional distribution)
    const columnWidths = [];
    const tableWidth = pageWidth - margins.left - margins.right;
    const numColumns = headers.length;
    
    // Give more space to the first column (agency name)
    const firstColumnRatio = 0.35; // 35% of width for first column
    const remainingWidthRatio = (1 - firstColumnRatio) / (numColumns - 1);
    
    for (let i = 0; i < numColumns; i++) {
      if (i === 0) {
        columnWidths.push(tableWidth * firstColumnRatio);
      } else {
        columnWidths.push(tableWidth * remainingWidthRatio);
      }
    }
    
    // Function to draw a row
    const drawRow = (rowData, y, isHeader = false, isFooter = false) => {
      let x = margins.left;
      
      // Set style based on row type
      if (isHeader) {
        pdf.setFont('helvetica', 'bold');
        pdf.setFillColor(240, 240, 240); // Light gray for header
        pdf.rect(x, y - lineHeight + 2, tableWidth, lineHeight - 1, 'F'); // Reduce rectangle height
      } else if (isFooter) {
        pdf.setFont('helvetica', 'bold');
      } else {
        pdf.setFont('helvetica', 'normal');
      }
      
      // Draw each cell in the row
      for (let i = 0; i < rowData.length; i++) {
        const cellContent = rowData[i] || '';
        const cellWidth = columnWidths[i];
        
        // Right align for numeric columns (all except the first)
        if (i > 0) {
          pdf.text(cellContent, x + cellWidth - 2, y, { align: 'right' });
        } else {
          pdf.text(cellContent, x + 2, y);
        }
        
        x += cellWidth;
      }
      
      // Restore normal font after header or footer
      if (isHeader || isFooter) {
        pdf.setFont('helvetica', 'normal');
      }
      
      return y + lineHeight;
    };
    
    // Extract data from rows
    const rows = dataRows.map(row => {
      return Array.from(row.querySelectorAll('td')).map(td => td.textContent || '');
    });
    
    // Extract data from footer
    const footerData = Array.from(footerRow.querySelectorAll('td')).map(td => td.textContent || '');
    
    // Calculate total number of pages
    const totalPages = Math.ceil(rows.length / rowsPerPage);
    
    // Draw each page
    for (let pageNum = 0; pageNum < totalPages; pageNum++) {
      // Add new page if not the first
      if (pageNum > 0) {
        pdf.addPage();
      }
      
      let y = margins.top;
      
      // Draw title and dates if it's the first page
      if (pageNum === 0) {
        // Title "Balance by agency"
        pdf.setFontSize(fontSize + 4); // Increase size for better visibility
        pdf.setFont('helvetica', 'bold');
        const titleParts = ['Balance por agencia', enterpriseName, productName].filter(Boolean);
        pdf.text(titleParts.join(' - '), pageWidth / 2, y + lineHeight, { align: 'center' });
        y += lineHeight * 2;
        
        // Dates
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', 'normal');
        // Get dates from component or use provided dates
        const dateText = `Desde ${fromDateStr} Hasta ${toDateStr} ${provider?.length > 0 ? ` de ${provider}` : ''}`;
        pdf.text(dateText, pageWidth / 2, y, { align: 'center' });
        y += lineHeight * 2.5; // More space after dates before table
      } else {
        y += lineHeight; // Additional space in subsequent pages
      }
      
      // Draw table header on each page
      y = drawRow(headers, y, true);
      
      // Calculate which rows go on this page
      const startRow = pageNum * rowsPerPage;
      const endRow = Math.min(startRow + rowsPerPage, rows.length);
      
      // Draw rows for this page
      for (let i = startRow; i < endRow; i++) {
        y = drawRow(rows[i], y);
        
        // Subtle dividing lines between rows (only if not the last row)
        if (i < endRow - 1) {
          pdf.setDrawColor(230, 230, 230);
          pdf.setLineWidth(0.1);
          pdf.line(margins.left, y - lineHeight / 2, pageWidth - margins.right, y - lineHeight / 2);
        }
      }
      
      // Draw footer only on the last page
      if (pageNum === totalPages - 1) {
        // Add space before footer
        y += lineHeight / 2;
        
        // Dividing line before footer
        pdf.setDrawColor(180, 180, 180);
        pdf.setLineWidth(0.5);
        pdf.line(margins.left, y - lineHeight / 2, pageWidth - margins.right, y - lineHeight / 2);
        
        // Draw footer
        drawRow(footerData, y, false, true);
        
        // Add user and date information at the bottom
        y += lineHeight * 2; // Space after footer
        
        // Get user information and current date from component
        const {
          user: { profile },
        } = getAppBarState().appState;
        const now = new Date();
        
        pdf.setFontSize(fontSize);
        pdf.setFont('helvetica', 'normal');
        const userDateText = `${profile.given_name} ${formatToShortLocalDateTime(now, 'DD-MM-YYYY h:mm:ss a')}`;
        pdf.text(userDateText, pageWidth - margins.right, y, { align: 'right' });
      }
    }
    
    pdf.save(`${filename}.pdf`);
  } catch (error) {
    console.error('Error generating PDF:', error);
    alert(`Error generating PDF: ${error.message || 'Unknown error'}`);
  }
}

// Print-specific styles for page size, orientation, and font
if (typeof window !== 'undefined') {
  const style = document.createElement('style');
  style.innerHTML = `
    @media print {
      @page {
        size: letter portrait;
        margin: 0mm;
      }
      body, .MuiPaper-root, .MuiTableCell-root, .MuiTypography-root, .MuiTable-root {
        font-family: Roboto, Arial, sans-serif !important;
        font-size: 12px !important;
      }
    }
  `;
  document.head.appendChild(style);
}
